import React, { useRef, useState } from 'react';
import { colors, fontFamily, fontSizes, fontWeights, radii } from '../../theme/tokens';

// Segmented OTP input: one box per digit. `value` is the full digit string.
const OtpInput = ({ value = '', onChange, length = 6, disabled = false, id, autoFocus = true }) => {
  const inputsRef = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  // Empty slots are stored as spaces so clearing a middle box doesn't shift later digits
  const digits = Array.from({ length }, (_, i) => (value[i] && value[i] !== ' ' ? value[i] : ''));
  const emit = (arr) => onChange?.(arr.map((d) => d || ' ').join('').trimEnd());

  const focusAt = (i) => {
    const el = inputsRef.current[Math.max(0, Math.min(length - 1, i))];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const setDigitsFrom = (start, incoming) => {
    const next = [...digits];
    let i = start;
    for (const ch of incoming) {
      if (i >= length) break;
      next[i] = ch;
      i += 1;
    }
    emit(next);
    focusAt(i >= length ? length - 1 : i);
  };

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) return;
    // Typing into a filled box yields two chars; keep only the new one. Longer input (autofill) fills forward.
    setDigitsFrom(i, raw.length === 2 && digits[i] ? raw.replace(digits[i], '') || raw[1] : raw);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[i]) {
        next[i] = '';
        emit(next);
      } else if (i > 0) {
        next[i - 1] = '';
        emit(next);
        focusAt(i - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const handlePaste = (i, e) => {
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!pasted) return;
    e.preventDefault();
    setDigitsFrom(i, pasted);
  };

  const box = (i) => {
    const focused = focusedIndex === i;
    return {
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: 56,
      height: 56,
      boxSizing: 'border-box',
      padding: 0,
      textAlign: 'center',
      borderRadius: radii.input,
      border: '1.5px solid ' + (focused ? colors.navy : digits[i] ? colors.navy : colors.softBlue),
      boxShadow: focused ? '0 0 0 3px ' + colors.softBlue : 'none',
      background: colors.white,
      fontFamily,
      fontSize: fontSizes.h3,
      fontWeight: fontWeights.semibold,
      color: colors.ink,
      outline: 'none',
      caretColor: colors.navy,
      transition: 'border-color 120ms, box-shadow 120ms',
    };
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
      {digits.map((d, i) => (
        <input
          key={i}
          id={i === 0 ? id : undefined}
          ref={(el) => { inputsRef.current[i] = el; }}
          type="tel"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${i + 1} of ${length}`}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          value={d}
          maxLength={i === 0 ? length : 2}
          style={box(i)}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => { setFocusedIndex(i); e.target.select(); }}
          onBlur={() => setFocusedIndex(-1)}
        />
      ))}
    </div>
  );
};

export default OtpInput;
